export type ReportCptSource =
  | "diagnosis"
  | "ihc"
  | "special_stain"
  | "other_stain"
  | "panel_group"
  | "panel_component_override"
  | "addendum";

export interface ReportCodeLike {
  code?: string | null;
  source?: ReportCptSource;
}

const DISPLAY_ONLY_CPT_MODIFIER = /(?:\s*[-/]\s*|\s+)(?:TC|CT|26)\s*$/i;

/** Strip report-inappropriate display modifiers without changing the base CPT. */
export function normalizeReportCptCode(
  code: string | null | undefined
): string | null {
  let normalized = code?.trim().toUpperCase() || "";
  if (!normalized) return null;

  // Historical imports sometimes represent the global component as a trailing
  // G. Limit that cleanup to a five-digit CPT so HCPCS codes such as G8482 and
  // Category II codes such as 0001F remain intact.
  normalized = normalized.replace(/^(\d{5})\s*G$/i, "$1");

  // Component modifiers may be composed more than once by legacy paths. Only
  // remove complete trailing tokens, never matching characters in a base code.
  while (DISPLAY_ONLY_CPT_MODIFIER.test(normalized)) {
    normalized = normalized.replace(DISPLAY_ONLY_CPT_MODIFIER, "").trim();
  }

  // A legacy display path concatenated modifiers without a delimiter. Restrict
  // this recovery to an exact five-digit CPT base so arbitrary alphanumeric
  // HCPCS codes are never truncated.
  normalized = normalized.replace(/^(\d{5})(?:(?:TC|CT|26))+$/i, "$1");
  normalized = normalized.replace(/^(\d{5})\s*G$/i, "$1");

  return normalized || null;
}

export function normalizeReportIcdCode(
  code: string | null | undefined
): string | null {
  const normalized = code?.trim().toUpperCase().replace(/\s+/g, "") || "";
  return normalized || null;
}

function compareCodes(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** Normalize, deterministically order, and aggregate CPT occurrences. */
export function formatReportCptCodes(
  codes: Array<string | null | undefined>
): string[] {
  const counts = (codes || []).reduce((result, value) => {
    const code = normalizeReportCptCode(value);
    if (code) result.set(code, (result.get(code) || 0) + 1);
    return result;
  }, new Map<string, number>());

  return Array.from(counts.entries())
    .sort(([a], [b]) => compareCodes(a, b))
    .map(([code, count]) => (count > 1 ? `${code} x${count}` : code));
}

/** ICD values are a unique set; repeated diagnoses never produce quantities. */
export function formatReportIcdCodes(
  codes: Array<string | null | undefined>
): string[] {
  const uniqueCodes = (codes || []).reduce((result, value) => {
    const code = normalizeReportIcdCode(value);
    if (code) result.add(code);
    return result;
  }, new Set<string>());
  return Array.from(uniqueCodes).sort(compareCodes);
}

/**
 * Live values represent only specimen diagnosis CPTs. Merge them additively
 * with backend stain CPTs so preview cannot hide reportable stain charges. `null` or
 * `undefined` means no live override (final/headless render); an empty array is
 * an intentional removal of all diagnosis CPTs and still retains stain CPTs.
 */
export function mergeReportCptCodes(
  entries: ReportCodeLike[],
  liveDiagnosisCodes?: Array<string | null | undefined> | null
): Array<string | null | undefined> {
  if (liveDiagnosisCodes == null)
    return (entries || []).map((entry) => entry.code);

  const stainCodes = (entries || [])
    .filter((entry) => Boolean(entry.source && entry.source !== "diagnosis"))
    .map((entry) => entry.code);

  return [...liveDiagnosisCodes, ...stainCodes];
}

export function selectReportIcdCodes(
  entries: ReportCodeLike[],
  liveDiagnosisCodes?: Array<string | null | undefined> | null
): Array<string | null | undefined> {
  if (liveDiagnosisCodes == null)
    return (entries || []).map((entry) => entry.code);
  const addendumCodes = (entries || [])
    .filter((entry) => entry.source === "addendum")
    .map((entry) => entry.code);
  return [...liveDiagnosisCodes, ...addendumCodes];
}
